// ============================================================
// SignIn.jsx
//
// Handles login via Email/Password and Google OAuth.
//
// Google login security:
//   - Checks if the Google account exists in Firestore users.
//   - If not registered → signs them out → redirects to /signup
//   - If registered → redirects based on courseAccess flag:
//       courseAccess: false → /user-dashboard
//       courseAccess: true  → /course-hub
// ============================================================

import React, { useState } from "react";
import {
    Grid, Card, Container, Typography, TextField,
    Button, Divider, CircularProgress, IconButton,
    InputAdornment, Box, styled,
} from "@mui/material";

import GoogleIcon from "@mui/icons-material/Google";
import EmailIcon from "@mui/icons-material/Email";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { auth, db } from "../firebase";
import {
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useNotification } from "./NotificationProvider";

// ============================================================
//  CONSTANTS
// ============================================================

const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

// ============================================================
//  STYLED COMPONENTS
// ============================================================

const PageWrapper = styled(Grid)(() => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

const AuthCard = styled(Card)(() => ({
    width: "100%",
    maxWidth: 900,
    padding: "40px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(0,0,0,0.1)",
    boxShadow: "none",
}));

const LeftPanel = styled(Box)(({ theme }) => ({
    paddingRight: "20px",
    borderRight: "1px solid rgba(0,0,0,0.1)",
    [theme.breakpoints.down("md")]: {
        borderRight: "none",
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        paddingBottom: "20px",
        marginBottom: "20px",
    },
}));

const RightPanel = styled(Box)(({ theme }) => ({
    paddingLeft: "20px",
    [theme.breakpoints.down("md")]: { paddingLeft: "0" },
}));

// ============================================================
//  HELPER FUNCTIONS
// ============================================================

/**
 * getUserProfile
 * Fetches the user's Firestore document.
 * Returns the data object or null if not found.
 *
 * Used to:
 *  1. Check if a Google user is registered.
 *  2. Check courseAccess flag to decide where to redirect.
 *
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
const getUserProfile = async (uid) => {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? snap.data() : null;
};

/**
 * redirectByAccess
 * Decides where to send a user after login based on their
 * courseAccess flag in Firestore.
 *
 *   courseAccess: true  → /course-hub  (admin has granted access)
 *   courseAccess: false → /user-dashboard (pending admin approval)
 *
 * @param {object} profile  - Firestore user document data
 * @param {function} navigate
 */
const redirectByAccess = (profile, navigate) => {
    if (profile.courseAccess === true) {
        navigate("/course-hub");
    } else {
        navigate("/user-dashboard");
    }
};

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function SignIn() {
    const { notify } = useNotification();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleLogin
     * Email/password login. After auth, checks Firestore profile
     * to determine correct redirect destination.
     */
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            // Check profile for courseAccess flag
            const profile = await getUserProfile(userCredential.user.uid);
            if (!profile) {
                // Account exists in Auth but not Firestore — edge case
                notify("Account setup incomplete. Please contact support.", "error");
                return;
            }

            notify("Login successful!", "success");
            redirectByAccess(profile, navigate);

        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    /**
     * handleGoogleLogin
     * Google OAuth login with registration check.
     *
     * Flow:
     *  1. Authenticate with Google.
     *  2. Check if UID exists in Firestore users.
     *  3. If not registered → sign out → redirect to /signup.
     *  4. If registered → redirect based on courseAccess flag.
     */
    const handleGoogleLogin = async () => {
        try {
            setLoading(true);
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);

            // Check if this Google account is registered
            const profile = await getUserProfile(userCredential.user.uid);

            if (!profile) {
                // Not registered — sign out and redirect to signup
                await signOut(auth);
                notify(
                    "No account found for this Google account. Please sign up first.",
                    "warning"
                );
                navigate("/signup");
                return;
            }

            notify("Google login successful!", "success");
            redirectByAccess(profile, navigate);

        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <PageWrapper container>
            <Container maxWidth="md">
                <AuthCard>
                    <Grid container spacing={4}>

                        {/* ── LEFT: Google Login ── */}
                        <Grid item xs={12} md={6}>
                            <LeftPanel>
                                <Typography variant="h5" fontWeight={800} mb={3} color="#100720">
                                    Welcome Back
                                </Typography>

                                <Button
                                    fullWidth variant="contained"
                                    startIcon={<GoogleIcon />}
                                    onClick={handleGoogleLogin}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: "#DB4437", color: "white",
                                        fontWeight: "bold", py: 1.2,
                                        "&:hover": { backgroundColor: "#c23321" }, mb: 3,
                                    }}
                                >
                                    {loading
                                        ? <CircularProgress size={24} color="inherit" />
                                        : "Sign in with Google"
                                    }
                                </Button>

                                <Divider sx={{ my: 2 }}>OR</Divider>

                                <Typography variant="body2" color="text.secondary" mb={3}>
                                    Use your email and password to sign in.
                                </Typography>

                                {/* Signup prompt */}
                                <Box
                                    p={2}
                                    sx={{
                                        backgroundColor: "#f9fafc",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(0,0,0,0.07)",
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        New here?{" "}
                                        <Link to="/signup" style={{ color: "#09122C", fontWeight: "bold" }}>
                                            Create an account
                                        </Link>
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                        Sign up to access your personal dashboard.
                                        Course access is granted after payment confirmation.
                                    </Typography>
                                </Box>
                            </LeftPanel>
                        </Grid>

                        {/* ── RIGHT: Email Login ── */}
                        <Grid item xs={12} md={6}>
                            <RightPanel>
                                <Typography variant="h5" fontWeight={800} mb={3} color="#100720">
                                    Login
                                </Typography>

                                <form onSubmit={handleLogin}>
                                    <TextField
                                        fullWidth label="Email Address" type="email"
                                        value={email} onChange={(e) => setEmail(e.target.value)}
                                        required
                                        InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1 }} /> }}
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        fullWidth label="Password"
                                        type={showPassword ? "text" : "password"}
                                        value={password} onChange={(e) => setPassword(e.target.value)}
                                        required
                                        InputProps={{
                                            startAdornment: <LockOpenIcon sx={{ mr: 1 }} />,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />

                                    <Button
                                        fullWidth type="submit" variant="contained"
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: "#09122C", color: "white",
                                            fontWeight: "bold", py: 1.2,
                                            "&:hover": { backgroundColor: "#142850" },
                                        }}
                                    >
                                        {loading
                                            ? <CircularProgress size={24} color="inherit" />
                                            : "Login"
                                        }
                                    </Button>
                                </form>
                            </RightPanel>
                        </Grid>

                    </Grid>
                </AuthCard>
            </Container>
        </PageWrapper>
    );
}
